import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  Navigate,
  useParams,
} from 'react-router-dom';

import { CallbackPage } from '@/components/auth/CallbackPage/CallbackPage';
import { AppShell } from '@/components/layout/AppShell';
import { useThemeColor, usePageTitle } from '@/hooks';
import { DashboardView } from '@/pages/DashboardPage';
import { MapView } from '@/components/views/MapView';
import { WorkspaceOnboardingPage } from '@/components/workspace/WorkspaceOnboardingPage';
import { SettingsPage } from '@/pages/SettingsPage/SettingsPage';
import { InvitationPage } from '@/pages/InvitationPage';
import { WorkspaceMapConfigPage } from '@/pages/WorkspaceMapConfigPage';
import { SharedMapPage } from '@/pages/SharedMapPage';
import { EmbedMapPage } from '@/pages/EmbedMapPage';
import { ProtectedRoute } from './ProtectedRoute';
import { RouteErrorElement } from './RouteErrorElement';
import { SettingsRedirect } from './SettingsRedirect';
import { WorkspaceRedirect } from './WorkspaceRedirect';
import { ROUTES } from './routes';

function RootLayout() {
  useThemeColor();
  usePageTitle();
  return <Outlet />;
}

/**
 * Legacy redirect: /w/:shortId/tasks → /w/:shortId/map/tasks
 * Kept for backward-compat with any cached links.
 */
function LegacyTasksRedirect() {
  const { shortId } = useParams<{ shortId: string }>();
  return <Navigate to={`/w/${shortId}/map/tasks`} replace />;
}

/**
 * Legacy redirect: /w/:shortId/notes → /w/:shortId/map/notes
 */
function LegacyNotesRedirect() {
  const { shortId } = useParams<{ shortId: string }>();
  return <Navigate to={`/w/${shortId}/map/notes`} replace />;
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorElement />,
    children: [
      {
        path: ROUTES.CALLBACK,
        element: <CallbackPage />,
      },
      {
        path: ROUTES.INVITATION,
        element: <InvitationPage />,
      },
      // Public, shell-less shared map (no ProtectedRoute / AppShell) — mirrors
      // the invitation route. Access is token-based via the public API.
      {
        path: ROUTES.SHARE,
        element: <SharedMapPage />,
      },
      {
        path: ROUTES.EMBED,
        element: <EmbedMapPage />,
      },
      {
        path: ROUTES.ONBOARDING,
        element: (
          <ProtectedRoute requireWorkspace={false}>
            <WorkspaceOnboardingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS,
        element: (
          <ProtectedRoute requireWorkspace={true}>
            <SettingsRedirect />
          </ProtectedRoute>
        ),
      },
      // Per-workspace map configurator — a top-level sibling of WORKSPACE
      // (outside the AppShell) so the first-run gate cannot loop on it.
      {
        path: ROUTES.WORKSPACE_MAP_CONFIG,
        element: (
          <ProtectedRoute requireWorkspace={true}>
            <WorkspaceMapConfigPage />
          </ProtectedRoute>
        ),
      },
      // New AppShell routes with nested views
      {
        path: ROUTES.WORKSPACE,
        element: (
          <ProtectedRoute requireWorkspace={true}>
            <AppShell />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: <DashboardView />,
          },
          // Unified /map route — MapView handles sub-routing internally
          // (background + overlay modal pattern via location.state).
          {
            path: 'map/*',
            element: <MapView />,
          },
          // Legacy redirects (backward-compat)
          {
            path: 'tasks',
            element: <LegacyTasksRedirect />,
          },
          {
            path: 'notes',
            element: <LegacyNotesRedirect />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
        ],
      },
      {
        path: '/',
        element: (
          <ProtectedRoute requireWorkspace={true}>
            <WorkspaceRedirect />
          </ProtectedRoute>
        ),
      },
      {
        path: '*',
        element: (
          <ProtectedRoute requireWorkspace={true}>
            <WorkspaceRedirect />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

/**
 * Application router component
 * Wraps RouterProvider with configured routes
 */
export function AppRouter() {
  return <RouterProvider router={router} />;
}
